import { IPersonalAddressState } from '@interfaces/mfe/mfe-personal-address.interface';
import { AccountTypes } from '@interfaces/models/account-types';
import { InvestmentObjectives } from '@interfaces/models/investment-objectives-types';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { OAAccountTypes } from '@interfaces/open-account/enums';
import { IEmploymentInfo, IOpenAccount } from '@interfaces/open-account/oa.interface';
import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { IRAOptions, IRATypes } from '@shared/models';
import { FinancialDetailsState } from '@store/reducers/income-net-worth/income-net-worth.reducer';

import { ITransferTypes, PersonalStates } from './oa.selectors';

export default function clientDataOAMapper(
  cooPersonalStates: PersonalStates,
  cooContactInfo: IPersonalAddressState,
  employment: Partial<VgEmploymentState> | undefined,
  financial: FinancialDetailsState,
  invObj: { objectives: InvestmentObjectives },
  transactionTypes: ITransferTypes,
  settlementFund: SettlementFundAoAEnum | null,
  isProspect: boolean = false,
): IOpenAccount {
  const oaPersonalInfo = [
    {
      firstName: cooPersonalStates.personalInfo.firstName,
      middleName: cooPersonalStates.personalInfo.middleName,
      suffix: cooPersonalStates.personalInfo.suffix,
      lastName: cooPersonalStates.personalInfo.lastName,
      email: cooPersonalStates.personalInfo.email,
      birthDate: cooPersonalStates.personalInfo.birthDate,
      citizenshipStatus: cooPersonalStates.personalInfo.citizenshipStatus,
      ssn_private: cooPersonalStates.personalInfo.ssn_private,
      gender: cooPersonalStates.personalInfo.gender,
      countryOfResidence: cooPersonalStates.personalInfo.countryOfCitizenship,
    },
  ];

  const oaDecedentInfo = cooPersonalStates.decedentInfo
    ? {
        firstName: cooPersonalStates.decedentInfo.firstName,
        middleName: cooPersonalStates.decedentInfo.middleName,
        lastName: cooPersonalStates.decedentInfo.lastName,
      }
    : {};

  const oaContactInfo = [
    {
      sameAsMailingAddress: cooContactInfo.isStreetAddressSame,
      mailingAddress: cooContactInfo.mailingAddress,
      streetAddress: cooContactInfo.streetAddress,
      primaryPhone: {
        phoneNumber: cooPersonalStates.personalPhone.phoneNumber,
        isMobile: cooPersonalStates.personalPhone.isMobile,
      },
    },
  ];

  const streetAddress = employment?.employmentStatus?.streetAddress;
  const associations = employment?.associations;

  const oaEmployment: IEmploymentInfo[] = [
    {
      employmentStatus: employment?.employmentStatus?.employmentStatus ?? '',
      employer: employment?.employmentStatus?.employer,
      occupation: employment?.employmentStatus?.occupation,
      address: streetAddress
        ? {
            street: streetAddress.street,
            city: streetAddress.city,
            state: streetAddress.state,
            zipCode: streetAddress.zipCode,
          }
        : undefined,
      association: associations
        ? ({
            spouse: associations.spouse,
            associated: associations.associated,
            control: associations.control,
            none: associations.none,
            controlCompanyTicker: associations.controlCompanyTicker
              ? Array.from(associations.controlCompanyTicker)
              : [],
            controlCompanyName: associations.controlCompanyName
              ? Array.from(associations.controlCompanyName)
              : [],
          } as unknown as IEmploymentInfo['association'])
        : undefined,
    },
  ];

  const oaGoals = invObj.objectives;

  const oaNetWorth = [
    {
      annualIncome: financial.annualIncome,
      liquidNetWorth: financial.liquidNetWorth,
    },
  ];

  const oaSources = {
    F: true,
  };

  const clientData = {
    selectedAccountTypeId: getOASelectedAccountTypeId(
      transactionTypes.accountType,
      transactionTypes.iraType,
      transactionTypes.iraOption,
    ),
    personalInfo: oaPersonalInfo,
    decedentInfo: oaDecedentInfo,
    contactInfo: oaContactInfo,
    fundingMethod: 'NONE', // TODO: Hardcoded but is it NONE or TRANSFER???
    employmentInfo: oaEmployment,
    goals: oaGoals,
    incomeNetWorth: oaNetWorth,
    sources: oaSources,
    isProspect: isProspect,
    settlementFund: settlementFund ?? SettlementFundAoAEnum.FED,
  };

  switch (clientData.selectedAccountTypeId) {
    case OAAccountTypes.TraditionalIRA:
    case OAAccountTypes.RothIRA:
    case OAAccountTypes.Individual:
      return clientData;
    default:
      return clientData;
  }
}

export function getOASelectedAccountTypeId(
  accountType: AccountTypes,
  iraType?: IRATypes,
  iraOption?: string,
): OAAccountTypes {
  switch (accountType) {
    case AccountTypes.INDV:
    case AccountTypes.JOINT:
    case AccountTypes.UGMA_UTMA:
      return OAAccountTypes.Individual;
    case AccountTypes.IRA:
      if (iraOption === IRAOptions.ASSUME) {
        return iraType === IRATypes.OTHER ? OAAccountTypes.TraditionalIRA : OAAccountTypes.RothIRA;
      } else {
        return iraType === IRATypes.OTHER || iraType === IRATypes.INHERITED_OTHER
          ? OAAccountTypes.InheritedIRA
          : OAAccountTypes.InheritedRothIRA;
      }
    default:
      return OAAccountTypes.UNKNOWN;
  }
}
